import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  V2ATransferDetailCopyItem,
  V2ATransferDetailItemAccountName,
  V2ATransferDetailItemCustomerName,
  V2ATransferDetailItemSentOn,
  V2ATransferDetailItemStatus,
  V2ATransferStatus,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useCancelTransaction } from 'features/accounts/hooks/useCancelTransaction';
import ConfirmCancelBottomSheet from 'features/v2a/sender/components/ConfirmCancelBottomSheet';
import RecipientListItem from 'features/v2a/sender/components/RecipientListItem';
import {
  getAvatarInitials,
  getAvatarTheme,
} from 'features/v2a/sender/helpers/Sender.helpers';
import useV2ATransfer from 'features/v2a/sender/hooks/useV2ATransfer';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import { SNACKBAR_DURATION } from 'shared/constants/snackbar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

type V2AItemType =
  | V2ATransferDetailCopyItem
  | V2ATransferDetailItemCustomerName
  | V2ATransferDetailItemSentOn
  | V2ATransferDetailItemStatus;

const isPaymentToType = (
  item:
    | V2ATransferDetailCopyItem
    | V2ATransferDetailItemAccountName
    | V2ATransferDetailItemCustomerName
    | V2ATransferDetailItemSentOn
    | V2ATransferDetailItemStatus,
): item is V2ATransferDetailItemCustomerName => {
  const title = (
    item as V2ATransferDetailItemCustomerName
  )?.title?.toLowerCase();
  return title === 'payment to';
};

const TransferDetails = () => {
  const router = useRouter();
  const { transferId } = useLocalSearchParams<{ transferId: string }>();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });
  const { bottom } = useSafeAreaInsets();
  const {
    transfer,
    fetching,
    error,
    refetchQuery: refetchDetails,
  } = useV2ATransfer({ transferId });
  const { defaultTitle } = useDefaultErrorStrings();

  const {
    cancelTransaction,
    cancelling,
    transactionCancelled: cancelTransactionSuccess,
    cancelTransactionError,
  } = useCancelTransaction();

  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [isTransactionCancelled, setIsTransactionCancelled] = useState(false);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'V2ATransferHistoryDetails',
  });

  const v2ATransactionCancellationSubmittedEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionCancellationSubmitted',
  });

  const v2ATransactionCancellationAbortedEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionCancellationAborted',
  });

  const v2ATransactionCancellationSuccessfulEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionCancellationSuccessful',
  });

  const v2ATransactionCancellationUnsuccessfulEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionCancellationUnsuccessful',
  });

  const v2ATransactionCancelledEvent = usePressableAnalytics({
    analyticsId: 'v2ATransactionCancelled',
    customAttributes: {
      cta: 'Retry',
    },
  });

  const openBottomSheet = () => bottomSheetRef.current?.present();
  const closeBottomSheet = () => {
    bottomSheetRef.current?.close();
  };

  const handleCancelTransactionPress = useCallback(() => {
    openBottomSheet();
  }, []);

  const handleConfirmCancelTransaction = () => {
    v2ATransactionCancellationSubmittedEvent();
    void cancelTransaction(transferId);
    closeBottomSheet();
  };

  const abortCancelTransaction = () => {
    v2ATransactionCancellationAbortedEvent();
    closeBottomSheet();
  };

  const getSnackbarText = () => {
    if (cancelTransactionSuccess) {
      return translatedText('transferDetails.cancelTransactionSuccess');
    }
    if (cancelTransactionError && 'message' in cancelTransactionError) {
      return cancelTransactionError.message;
    }
    return translatedText('transferDetails.cancelTransactionFailed');
  };

  const handleSnackbarRetry = () => {
    setShowSnackBar(false);
    v2ATransactionCancelledEvent();
    void cancelTransaction(transferId);
  };

  useEffect(() => {
    if (cancelTransactionSuccess || cancelTransactionError) {
      setShowSnackBar(true);
    }

    if (cancelTransactionSuccess) {
      v2ATransactionCancellationSuccessfulEvent();
      refetchDetails();
      setIsTransactionCancelled(true);
    }

    if (cancelTransactionError) {
      v2ATransactionCancellationUnsuccessfulEvent();
    }
    // disabling due to timeout error in test cases
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelTransactionError, cancelTransactionSuccess, refetchDetails]);

  const handleBackPress = () => {
    if (isTransactionCancelled) {
      router.replace('/move-money/v2a/transfer-history');
    } else {
      router.back();
    }
  };

  useBackHandler(handleBackPress);

  if (!transfer?.details || error) {
    return (
      <ScreenTransitionOverlay
        isLoading={fetching || cancelling}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={error.message ?? ''}
              tryAgainAction={refetchDetails}
            />
          ) : null
        }
      />
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              options={{
                title: translatedText('transferDetails.title'),
                headerBackVisible: true,
              }}
              primaryOnPress={handleBackPress}
            />
          ),
        }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View key={`${transfer.id}`} style={styles.scrollViewContent}>
          <View style={styles.valueContainer}>
            <AmplitudeMask>
              <ExpressiveLgText numberOfLines={1}>
                {transfer?.value.formattedWithPlusPrefixOrAbs ??
                  transfer?.value.formatted}
              </ExpressiveLgText>
            </AmplitudeMask>
          </View>

          {transfer?.details.map((group, index) => {
            const isLastGroup = index === transfer.details.length - 1;
            const isFirstGroup = index === 0;
            const groupKey = `${index}`;
            return (
              <View key={groupKey}>
                {group?.items?.map((item: V2AItemType, indexChild) => {
                  const showsDivider = indexChild < group.items.length - 1;
                  const key = `${groupKey}-${item.id}`;

                  if (isPaymentToType(item)) {
                    return (
                      <AmplitudeMask
                        blockStyleMask
                        key={key}
                        style={styles.amplitudeMask}
                      >
                        <RecipientListItem
                          id={item.id}
                          subTitle={item.title ?? ''}
                          title={item.copy ?? ''}
                          avatarTheme={getAvatarTheme(
                            getAvatarInitials(transfer?.to?.displayName),
                          )}
                          lineFormat={ListItemLineFormat.LABEL}
                          showVaroLogo={false}
                        />
                      </AmplitudeMask>
                    );
                  } else if (item.copy) {
                    return (
                      <AmplitudeMask key={key} style={styles.amplitudeMask}>
                        <ListItem
                          primaryCopy={item.copy}
                          secondaryCopy={item.title ?? ''}
                          lineFormat={ListItemLineFormat.LABEL}
                          showsChevron={false}
                        />
                        {showsDivider ? (
                          <View style={styles.separatorWrapper}>
                            <HairLineDivider />
                          </View>
                        ) : null}
                      </AmplitudeMask>
                    );
                  }

                  return null;
                })}
                {group?.items?.length > 0 && isFirstGroup ? (
                  <View style={styles.separatorWrapper}>
                    <HairLineDivider />
                  </View>
                ) : null}
                {group?.items && group?.items?.length === 0 && !isLastGroup ? (
                  <View style={styles.divider} />
                ) : null}
              </View>
            );
          })}
        </View>
        {transfer?.status === V2ATransferStatus.Pending &&
        transfer.isCancellable ? (
          <View style={styles.buttonsWrapper}>
            <Button
              isLoading={cancelling}
              label={translatedText('transferDetails.cancelTransaction')}
              onPress={handleCancelTransactionPress}
              analyticsId="v2ATransactionCancelled"
              customAttributes={{ cta: 'Cancel transaction' }}
            />
          </View>
        ) : null}
      </ScrollView>

      <ConfirmCancelBottomSheet
        bottomSheetRef={bottomSheetRef}
        onConfirm={handleConfirmCancelTransaction}
        onCancel={abortCancelTransaction}
        confirmationText={translatedText(
          'transferDetails.cancelConfirmationText',
        )}
        confirmButtonLabel={translatedText('transferDetails.yes')}
        cancelButtonLabel={translatedText('transferDetails.no')}
      />
      {showSnackBar ? (
        <Snackbar
          key={getUuid()}
          text={getSnackbarText()}
          duration={SNACKBAR_DURATION}
          buttonOnPress={
            cancelTransactionError ? handleSnackbarRetry : undefined
          }
          buttonLabel={
            cancelTransactionError
              ? translatedText('transferDetails.retry')
              : undefined
          }
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  amplitudeMask: {
    flex: 1,
  },
  buttonsWrapper: {
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  separatorWrapper: {
    paddingHorizontal: theme.spacing.m,
  },
  valueContainer: {
    alignItems: 'center',
    paddingVertical: theme.spacing.l,
  },
});

export default TransferDetails;
